import {
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from "@/components/ui/dialog";
import SectionNavigationSidebar from "@/components/ui/section-navigation-dialog/SectionNavigationSidebar";
import { type Key, useCallback, useMemo, useRef, useState } from "react";
import { TabList, TabPanel, Tabs } from "react-aria-components";
import StyledTab from "./StyledTab";
import type { TabbedDialogProps } from "./TabbedDialog.types";
import { useScrollSync } from "./useScrollSync";

/**
 * A reusable tabbed dialog component that combines:
 * - A modal dialog wrapper
 * - A tab bar for switching between different content views
 * - A navigation sidebar that syncs with scrollable content
 *
 * The component supports both controlled and uncontrolled modes for tab selection.
 *
 * @example
 * ```tsx
 * const tabs: TabConfig[] = [
 *   { id: 'overview', label: 'Overview', sections: overviewSections },
 *   { id: 'details', label: 'Details', sections: detailSections },
 * ];
 *
 * <TabbedDialog
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   title="My Dialog"
 *   tabs={tabs}
 *   renderContent={({ activeTabId, activeSectionId }) => (
 *     <MyContent tab={activeTabId} section={activeSectionId} />
 *   )}
 * />
 * ```
 */
const TabbedDialog = ({
  isOpen,
  onClose,
  title,
  tabs,
  defaultTabId,
  activeTabId: controlledTabId,
  onTabChange,
  renderContent,
}: TabbedDialogProps) => {
  const contentRef = useRef<HTMLDivElement | null>(null);

  // Determine if we're in controlled mode
  const isControlled = controlledTabId !== undefined;

  // Internal state for uncontrolled mode
  const [internalTabId, setInternalTabId] = useState<string>(
    defaultTabId || tabs[0]?.id || "",
  );

  // Use controlled or internal tab ID
  const activeTabId = isControlled ? controlledTabId : internalTabId;

  // Find the current tab configuration
  const currentTab = useMemo(
    () => tabs.find((tab) => tab.id === activeTabId) || tabs[0],
    [tabs, activeTabId],
  );

  // Get sections for the current tab
  const sections = currentTab?.sections || [];

  // Set up scroll synchronization
  const { activeSection, scrollToSection } = useScrollSync(
    contentRef,
    sections,
    isOpen,
    activeTabId,
  );

  // Handle tab selection change
  const handleTabChange = useCallback(
    (key: Key) => {
      const newTabId = key.toString();

      if (isControlled) {
        onTabChange?.(newTabId);
      } else {
        setInternalTabId(newTabId);
      }
    },
    [isControlled, onTabChange],
  );

  // Reset scroll position when tab changes
  const handleTabChangeWithReset = useCallback(
    (key: Key) => {
      handleTabChange(key);

      // Reset scroll position to top when changing tabs
      if (contentRef.current) {
        contentRef.current.scrollTo({ top: 0, behavior: "instant" });
      }
    },
    [handleTabChange],
  );

  return (
    <DialogOverlay isOpen={isOpen}>
      <DialogContent
        onClose={onClose}
        size="lg"
        className="h-[calc(100vh-2rem)]"
      >
        {title && (
          <DialogHeader>
            <DialogTitle className="font-serif text-lg">{title}</DialogTitle>
          </DialogHeader>
        )}

        <DialogBody className="p-0">
          <Tabs
            selectedKey={activeTabId}
            onSelectionChange={handleTabChangeWithReset}
            className="h-full flex flex-col py-1"
          >
            <TabList
              aria-label="Content tabs"
              className="flex gap-4 border-gray-100 border-b px-2 shrink-0"
            >
              {tabs.map((tab) => (
                <StyledTab key={tab.id} id={tab.id}>
                  {tab.label}
                </StyledTab>
              ))}
            </TabList>

            {tabs.map((tab) => (
              <TabPanel key={tab.id} id={tab.id} className="flex-1 min-h-0">
                <div className="flex h-full">
                  <SectionNavigationSidebar
                    sections={sections}
                    activeSection={activeSection}
                    onNavigate={scrollToSection}
                  />

                  <div className="flex-1 overflow-hidden rounded-br-lg">
                    <div
                      ref={tab.id === activeTabId ? contentRef : null}
                      className="h-full overflow-y-auto p-6"
                    >
                      {renderContent({
                        activeTabId,
                        activeSectionId: activeSection,
                      })}
                    </div>
                  </div>
                </div>
              </TabPanel>
            ))}
          </Tabs>
        </DialogBody>
      </DialogContent>
    </DialogOverlay>
  );
};

export default TabbedDialog;
